import dayjs from "dayjs";
import { isEmpty, sum } from "lodash";
import { useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { useMROIStore } from "@/pages/channel-roi/store/useMROIStore";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import { Layer, LAYER_TO_DISPLAY_NAME } from "@/Utils/hierarchy";

import { useChannelROIInsights } from "./api/getChannelROIInsights";

import { useSharedRevenue } from "./api/getSharedRevenue";
import AdStockBarGraph from "./components/Sections/AdStocks/AdStockBarGraph";
import ChannelEffectiveness from "./components/Sections/ChannelEffectiveness/ChannelEffectiveness";
import InstantInsightsContainer from "./components/Sections/InstantInsights/InstantInsightsContainer";
import MarketingMetrics from "./components/Sections/MarketingMetrics/MarketingMetrics";
import { content, noDataContent } from "./constants/content";
import { MROIChartType } from "./constants/types";
import { FunnelStage } from "./filters";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { combineNodeData } from "./helpers/combineNodeData";
import { createTotalRow } from "./helpers/createTotalRow";
import { getDefaultColumns } from "./helpers/getDefaultColumns";
import { getMetrics } from "./helpers/getMetrics";
import { mergeSharedRevenueData } from "./helpers/mergeSharedRevenueData";
import { useAvailableFilters } from "./hooks/useAvailableFilters";
import { useDebouncedInsights } from "./hooks/useDebouncedInsights";
import { useRois } from "./api/getRois";

const MROI = () => {
  const { startDate, endDate } = useDateRangeStore();
  const { config } = useConfig();
  const apiRef = useRef(null);
  const { currentOrganization, initInfo, nodes, loading } = useInfo();
  const { moduleOpened } = useAnalytics();
  const metricOptions = useMetricOptions();

  const hendrenTier = currentOrganization?.configuration?.hendrenTier;

  const isInsightsEnabled = hendrenTier !== "OFF";

  const availableFilters = useAvailableFilters();

  const {
    targetNodeUUID: selectedNodeUUID,
    funnelStage,
    geographies,
    channels,
    compareNodeUUIDs,
  } = useMROIStore((state) => state.filters);

  const { activeColumns, geoView, businessUnitToggle, metricType } = useMROIStore(
    (state) => state.grid
  );
  const chartType = useMROIStore((state) => state.chart.channelEffectiveness.chartType);

  const { setTargetNodeUUID, setActiveColumns, setGeoView, setBusinessUnitToggle, setMetricType } =
    useMROIStore();

  const currencySymbol = config.currency.getSymbol();
  const formatCurrency = config.currency.formatCurrency;

  const selectedNode = nodes?.getNode(selectedNodeUUID ?? "");

  const areParamsValid = startDate && endDate && selectedNodeUUID;

  const noBrandLayer = !nodes?.getAvailableLayers().includes(Layer.Brand);
  const nodeLayer = nodes?.getNodeLayer(selectedNodeUUID ?? "") ?? Layer.Unknown;

  const { debouncedParams, isValid } = useDebouncedInsights({
    startDate,
    endDate,
    selectedNodeUUID,
    funnelStage,
    channels,
    geographies,
    selectedNode,
  });

  const insightsApiEnabled = !!isValid && isInsightsEnabled;

  const { data: roiData, isLoading: isRoisLoading } = useRois({
    startDate: startDate,
    endDate: endDate,
    brand: selectedNode?.node.brand ?? "",
    category: selectedNode?.node.category ?? "",
    product: selectedNode?.node.product ?? "",
    shouldShiftSpend: noBrandLayer,
    channels: channels ?? [],
    funnelStage: funnelStage ?? "all",
    geographies: geographies,
    businessUnitToggle,
    nodeUuids: compareNodeUUIDs.length > 0 ? compareNodeUUIDs : [],
    config: {
      enabled: !!areParamsValid && !!selectedNode && !!funnelStage,
    },
  });

  const { data: sharedRevenueData, isLoading: isSharedLoading } = useSharedRevenue({
    brand: selectedNode?.node.brand ?? "",
    category: selectedNode?.node.category ?? "",
    product: selectedNode?.node.product ?? "",
    startDate: startDate,
    endDate: endDate,
    shouldShiftSpend: noBrandLayer,
    channels: channels ?? [],
    funnelStage: funnelStage ?? "all",
    geographies: geographies,
    businessUnitToggle,
    config: {
      enabled: !!areParamsValid && !!selectedNode && !!funnelStage,
    },
  });

  const {
    data: channelROIInsightsData,
    isLoading: isChannelROIInsightsLoading,
    error: channelROIInsightsError,
  } = useChannelROIInsights({
    startDate: debouncedParams.startDate,
    endDate: debouncedParams.endDate,
    brand: debouncedParams.brand,
    category: debouncedParams.category,
    product: debouncedParams.product,
    funnel: LAYER_TO_DISPLAY_NAME[debouncedParams.funnelStage as Layer] ?? "all",
    channels: debouncedParams.channels,
    geos: debouncedParams.geographies,
    shouldShiftSpend: noBrandLayer,
    config: {
      enabled: insightsApiEnabled,
      retry: false,
    },
  });

  const [rois, ...comparisonNodes] = roiData ?? [];
  if (rois) {
    rois.nodeUuid = selectedNodeUUID || "";
  }

  const combinedNodeData =
    rois && comparisonNodes.length > 0
      ? combineNodeData([rois, ...comparisonNodes], nodes)
      : undefined;

  const comparisonNodeData = Object.values(combinedNodeData?.aggregated || {}).filter(
    (data) => data.nodeUuid !== selectedNodeUUID
  );

  const hasSOE = !isEmpty(sharedRevenueData?.aggregated || {});

  const columns = getColumnDefinitions(activeColumns, {
    currencySymbol,
    formatCurrency,
    geoView,
    funnelStage: (funnelStage ?? "all") as FunnelStage,
    layer: nodeLayer,
    businessUnitToggle,
    metricType,
    hasSOE,
    config,
  });

  useEffect(() => {
    if (nodes && !loading && selectedNodeUUID === null) {
      const defaultNodeUUID = nodes.getDefaultNode()?.node.uuid;

      if (defaultNodeUUID !== undefined) {
        setTargetNodeUUID(defaultNodeUUID);
      }
    }
  }, [loading, selectedNodeUUID, nodes]);

  useEffect(() => {
    setActiveColumns(getDefaultColumns(funnelStage, nodeLayer));
  }, [funnelStage, nodeLayer]);

  const nodeData =
    chartType === MROIChartType.BUBBLE && compareNodeUUIDs.length > 0 ? combinedNodeData : rois;

  const view =
    geoView === GeoViewToggle.SPLIT || businessUnitToggle === BusinessUnitToggle.SPLIT
      ? nodeData?.split
      : nodeData?.aggregated;

  // TODO: Add shared view Aggregated / Split
  const sharedView =
    geoView === GeoViewToggle.SPLIT || businessUnitToggle === BusinessUnitToggle.SPLIT
      ? sharedRevenueData?.split
      : sharedRevenueData?.aggregated;

  const summaryMetrics = getMetrics(
    rois?.totals,
    sharedRevenueData?.totals,
    config,
    nodeLayer,
    funnelStage ?? "all"
  );

  const hasNoData =
    isEmpty(sharedRevenueData?.aggregated) &&
    isEmpty(rois?.aggregated) &&
    !isRoisLoading &&
    !isSharedLoading;

  useEffect(() => {
    moduleOpened("mroi", nodeLayer);
  }, []);

  if (loading || nodes === null || selectedNodeUUID === null || isRoisLoading) {
    return (
      <Box>
        <Box marginBottom={3}>
          <Title title={content.title} subtitle={content.subtitle} />
          <Header availableFilters={availableFilters} />
          <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
            <CircularProgress size={150} />
          </Stack>
        </Box>
      </Box>
    );
  }

  const newTotal = sum([
    rois?.totals.totalRevenue ?? 0,
    sharedRevenueData?.totals.totalRevenue ?? 0,
  ]);

  const mergedAggregatedData = mergeSharedRevenueData(
    nodeData?.aggregated ?? {},
    sharedRevenueData?.aggregated ?? {},
    newTotal
  );

  const mergedSplitData = mergeSharedRevenueData(
    nodeData?.split ?? {},
    sharedRevenueData?.split ?? {},
    newTotal
  );

  const mergedView =
    geoView === GeoViewToggle.SPLIT || businessUnitToggle === BusinessUnitToggle.SPLIT
      ? mergedSplitData
      : mergedAggregatedData;
  const rows = Object.values(mergedView ?? {});

  // Create total row for pinning at the top
  const totalRow = createTotalRow(mergedView ?? {});

  const toggles: Toggle[] = [
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: BusinessUnitToggle.AGGREGATE },
        { label: "Split by Business Unit", value: BusinessUnitToggle.SPLIT },
      ],
    },
  ];

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  const [geoData] = initInfo?.geographies || [];

  if (geoData && geoData.length > 0) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoView,
      options: [
        { label: "Aggregate Geographies", value: GeoViewToggle.AGGREGATE },
        { label: "Split by Geography", value: GeoViewToggle.SPLIT },
      ],
    });
  }

  const data = chartType === MROIChartType.BUBBLE ? rois?.aggregated : mergedAggregatedData;

  return (
    <Box>
      <Box marginBottom={3}>
        <Title title={content.title} subtitle={content.subtitle} />
        <Header availableFilters={availableFilters} />
        {hasNoData ? (
          <NoData title={noDataContent.title} description={noDataContent.description} />
        ) : (
          <>
            <Box my={3} mx={4}>
              <MarketingMetrics allMetrics={summaryMetrics} />
            </Box>
            {isInsightsEnabled && !channelROIInsightsData?.error && (
              <InstantInsightsContainer
                boxProps={{ my: 3, mx: 4 }}
                insightsData={channelROIInsightsData}
                isLoading={isChannelROIInsightsLoading}
                error={channelROIInsightsError?.message || channelROIInsightsData?.error}
                startDate={dayjs(startDate).format("MMM DD, YYYY")}
                endDate={dayjs(endDate).format("MMM DD, YYYY")}
              />
            )}
            <ChannelEffectiveness
              data={data ?? {}}
              comparisonNodeData={comparisonNodeData}
              nodeLayer={nodeLayer}
              hasSharedRevenue={!isEmpty(sharedRevenueData?.aggregated)}
              hasRoiRevenue={!isEmpty(rois?.aggregated)}
            />
            <AdStockBarGraph data={rois?.aggregated ?? {}} />
            {(view || sharedView) && (
              <Box mx={4}>
                <DataGridContainer
                  apiRef={apiRef}
                  rows={rows ?? []}
                  columns={columns}
                  pinnedRows={{
                    top: [totalRow],
                  }}
                  metricDropdown={metricDropdownConfig}
                  toolbarComponent={
                    <ToolBarShared
                      apiRef={apiRef}
                      columns={columns.filter((column) => column.filterable !== false)}
                      activeColumns={activeColumns}
                      toggles={toggles}
                      handleApplyFilters={({ activeColumns, toggles }) => {
                        const geoViewToggle = toggles["geoViewToggle"];
                        const businessUnitToggleValue = toggles["businessUnitToggle"];

                        setActiveColumns(activeColumns);

                        if (geoViewToggle) {
                          setGeoView(geoViewToggle as GeoViewToggle);
                        }

                        if (businessUnitToggleValue) {
                          setBusinessUnitToggle(businessUnitToggleValue as BusinessUnitToggle);
                        }
                      }}
                      handleResetFilters={() => {
                        setActiveColumns(getDefaultColumns(funnelStage, nodeLayer));
                        setGeoView(GeoViewToggle.AGGREGATE);
                        setMetricType(MetricType.MARKETING_ROI);
                        setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
                      }}
                    />
                  }
                  options={{
                    rowHeight: 52,
                    sortModel: [{ field: "total_spend", sort: "desc" }],
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MROI;
